import { useState, useEffect, useRef } from 'react'
import { Menu, Languages, Sun, Moon, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from './ui/badge'
import { cn } from '@/lib/utils'
import { translate, useT } from '@/lib/i18n'
import { SIDEBAR_WIDTH } from '@/layout/AppLayout'

const FONT_LABELS = { sm: 'A-', md: 'A', lg: 'A+' }
const FONT_CYCLE  = { sm: 'md', md: 'lg', lg: 'sm' }

const FG_CLS  = 'text-[hsl(var(--sidebar-foreground))]'
const BTN_CLS = `${FG_CLS} hover:bg-[hsl(var(--sidebar-accent))]/60 hover:text-white`

// Bulle de badge en haut à droite du bouton (positionnement propre à la toolbar — le composant
// Badge partagé reste un simple badge inline, réutilisé tel quel ici avec un wrapper positionné).
function CornerBadge({ value }) {
  if (value == null || value === '') return null
  return <span className="absolute -top-1 -right-1"><Badge value={value} /></span>
}

function ToolbarText({ btn }) {
  return <div id={btn.id} className={cn('shrink-0 text-sm', FG_CLS)} dangerouslySetInnerHTML={{ __html: btn.defaultValue ?? '' }} />
}

// Plus de 2 langues : liste déroulante pour choisir directement plutôt que de cycler à l'aveugle.
function ToolbarLanguages({ btn, lang, onEvent }) {
  const items = (btn.languages ?? []).filter(l => !l[3])
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = e => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [open])

  const select = code => {
    onEvent(btn.id, code, 'Languages')
    setOpen(false)
  }

  const cycle = () => {
    if (items.length === 0) return
    const idx = items.findIndex(l => l[0] === lang)
    select(items[(idx + 1) % items.length][0])
  }

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <Button id={btn.id} size="icon-lg" variant="ghost" onClick={items.length > 2 ? () => setOpen(v => !v) : cycle} title={btn.tooltip}
        className={cn(BTN_CLS, 'gap-1 px-2 w-auto')}>
        <Languages size={22} />
        <span className="text-sm font-semibold">{(lang ?? '').toUpperCase()}</span>
      </Button>
      {open && items.length > 2 && (
        <ul className="absolute top-full right-0 mt-1 min-w-[140px] z-50 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-md py-1">
          {items.map(l => (
            <li key={l[0]}
              className={cn('px-3 py-1.5 text-sm cursor-pointer hover:bg-[hsl(var(--accent))]', l[0] === lang && 'font-semibold')}
              onMouseDown={() => select(l[0])}
            >
              {l[1]}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ToolbarFontSize({ btn, fontSize, onEvent }) {
  return (
    <Button id={btn.id} size="icon-lg" variant="ghost" title={btn.tooltip ?? `Taille de police : ${FONT_LABELS[fontSize]}`}
      onClick={() => onEvent(btn.id, FONT_CYCLE[fontSize], 'FontSize')} className={BTN_CLS}>
      <span className="text-base font-bold leading-none">{FONT_LABELS[fontSize]}</span>
    </Button>
  )
}

function ToolbarMode({ btn, darkMode, onEvent }) {
  return (
    <Button id={btn.id} size="icon-lg" variant="ghost" title={btn.tooltip ?? (darkMode ? 'Mode clair' : 'Mode sombre')}
      onClick={() => onEvent(btn.id, darkMode ? 'light' : 'dark', 'Mode')} className={BTN_CLS}>
      {darkMode ? <Sun size={24} /> : <Moon size={24} />}
    </Button>
  )
}

function ToolbarButtonGeneric({ btn, onEvent, exit }) {
  return (
    <div className="relative shrink-0">
      <Button id={btn.id} size="icon-lg" variant="ghost" title={btn.tooltip}
        onClick={() => onEvent(btn.id, '', exit ? 'Exit' : 'Button')}
        className={exit ? 'text-[hsl(var(--sidebar-foreground))] hover:bg-red-500/20 hover:text-red-400' : BTN_CLS}>
        {btn.icon && <i className={cn(btn.icon, 'text-[1.6em]')} aria-hidden="true" />}
      </Button>
      <CornerBadge value={btn.badge} />
    </div>
  )
}

// Pas de connaissance de l'état courant du chat (visible/caché) — toggleChatButton() (tbsUserMessages,
// UserMessages.jsx) le détermine lui-même via sa propre ref synchrone et bascule en conséquence ;
// ce bouton se contente de déclencher l'appel à l'aveugle, puis de remonter l'événement standard
// (comme Button/Exit, value='' : aucune valeur de retour pertinente à transmettre ici).
function ToolbarChatState({ btn, onEvent }) {
  const handleClick = () => {
    window.tbsUserMessages?.toggleChatButton?.()
    onEvent(btn.id, '', 'ChatState')
  }
  return (
    <div className="relative shrink-0">
      <Button id={btn.id} size="icon-lg" variant="ghost" title={btn.tooltip} onClick={handleClick} className={BTN_CLS}>
        {btn.icon && <i className={cn(btn.icon, 'text-[1.6em]')} aria-hidden="true" />}
      </Button>
      <CornerBadge value={btn.badge} />
    </div>
  )
}

function ToolbarDynamicCombo({ btn, onEvent }) {
  const t = useT()
  const [open, setOpen]           = useState(false)
  const [inputText, setInputText] = useState('')
  const [options, setOptions]     = useState([])
  const debounce  = useRef(null)
  const wrapperRef = useRef(null)

  // `mousedown` et non `click` : le clic qui ouvre remplace le <button> (contenant la cible du
  // clic, le <svg>) par un <input> dans le même rendu — au moment où ce même clic termine sa
  // remontée jusqu'à `document`, la cible d'origine est déjà détachée du DOM, et
  // `wrapperRef.contains(e.target)` renvoie alors `false` (un nœud détaché ne peut être contenu
  // nulle part), fermant aussitôt le composant qui vient de s'ouvrir. `mousedown` précède `click`
  // dans la séquence native : l'effet (déclenché par `open`) ne s'attache qu'après le commit du
  // clic ayant ouvert, donc après le `mousedown` de cette même interaction — il ne peut donc
  // jamais se déclencher rétroactivement pour l'ouverture en cours.
  useEffect(() => {
    if (!open) return
    const handler = e => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleSearch = e => {
    const token = e.target.value
    setInputText(token)
    if (!token || token.length < 3) { setOptions([]); return }
    clearTimeout(debounce.current)
    debounce.current = setTimeout(async () => {
      try {
        const items = await window.onTbsToolbarComboLoader(btn.id, token)
        setOptions(Array.isArray(items) ? items : [])
      } catch (e) {
        console.error(e)
        setOptions([])
      }
    }, 300)
  }

  const close = () => { setOpen(false); setInputText(''); setOptions([]) }

  const select = opt => {
    onEvent(btn.id, opt[0], 'DynamicCombo')
    close()
  }

  // Fermé : simple bouton loupe. Ouvert : remplacé sur place par un Input qui prend sa vraie place
  // dans le flux flex de la toolbar (pas un menu flottant en `position: absolute` par-dessus le
  // contenu) — la zone `path`/`flex-1` (flexible, voir Toolbar()) absorbe cet élargissement en
  // premier, ce qui pousse visuellement les boutons précédents (à gauche) sans code dédié, le
  // groupe de boutons restant ancré à droite. Seule la liste de suggestions reste en `absolute`,
  // sous l'input, même style que l'ancien menu déroulant.
  return (
    <div ref={wrapperRef} className="relative shrink-0 flex items-center">
      {open ? (
        <div className="relative">
          {/* Loupe décorative, même convention que le combo proxyLoader (ComboField.jsx) :
              positionnée en absolute dans l'input, non interactive (`pointer-events-none`) —
              le déclenchement de la recherche reste piloté par `onChange` (debounce 300ms). */}
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] pointer-events-none"
          />
          <Input
            id={btn.id}
            autoFocus
            value={inputText}
            onChange={handleSearch}
            onKeyDown={e => { if (e.key === 'Escape') close() }}
            placeholder={t('searchMinChars')}
            className="h-8 w-60 pl-8"
          />
          {options.length > 0 && (
            <ul className="absolute top-full left-0 right-0 mt-1 max-h-52 overflow-y-auto z-50 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-md py-1">
              {options.map((opt, i) => opt[3]
                ? <li key={i} className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{opt[1]}</li>
                : <li key={i} className="px-2 py-1.5 text-sm cursor-pointer hover:bg-[hsl(var(--accent))]" onMouseDown={() => select(opt)}>{opt[1]}</li>
              )}
            </ul>
          )}
        </div>
      ) : (
        <Button id={btn.id} size="icon-lg" variant="ghost" title={btn.tooltip}
          onClick={() => setOpen(true)} className={BTN_CLS}>
          <Search size={24} />
        </Button>
      )}
    </div>
  )
}

function ToolbarButton({ btn, lang, fontSize, darkMode, onEvent }) {
  switch (btn.type) {
    case 'Text':         return <ToolbarText btn={btn} />
    case 'Languages':    return <ToolbarLanguages btn={btn} lang={lang} onEvent={onEvent} />
    case 'FontSize':     return <ToolbarFontSize btn={btn} fontSize={fontSize} onEvent={onEvent} />
    case 'Mode':         return <ToolbarMode btn={btn} darkMode={darkMode} onEvent={onEvent} />
    case 'Exit':         return <ToolbarButtonGeneric btn={btn} onEvent={onEvent} exit />
    case 'DynamicCombo': return <ToolbarDynamicCombo btn={btn} onEvent={onEvent} />
    case 'ChatState':    return <ToolbarChatState btn={btn} onEvent={onEvent} />
    case 'Button':
    default:              return <ToolbarButtonGeneric btn={btn} onEvent={onEvent} />
  }
}

export default function Toolbar({
  toolbarData, visible, buttonOverrides,
  sidebarOpen, onToggleSidebar,
  lang, fontSize, darkMode, onButtonEvent,
}) {
  const logo = toolbarData?.logo
  const path = toolbarData?.path
  const buttons = toolbarData?.buttons ?? []
  const hideHamburger = toolbarData?.hideHamburger === true

  return (
    <header
      // Dégradé "Option D — couleurs de marque" (voir src/index.css, --sidebar-gradient) plutôt
      // qu'un aplat hsl(var(--sidebar)) — même fond que le menu de gauche (AppLayout.jsx).
      style={{ background: 'var(--sidebar-gradient)', ...(visible === false ? { display: 'none' } : undefined) }}
      className="flex items-center h-[55px] px-3 gap-1 border-b border-[hsl(var(--sidebar-border))]"
    >
      {logo && (
        <a href="/" title={translate(lang, 'home')} className="shrink-0 h-full flex items-center justify-center" style={{ width: SIDEBAR_WIDTH }}>
          {/* Décalage de 13px vers la gauche pour recentrer visuellement le logo par rapport au
              menu de gauche (largeur SIDEBAR_WIDTH) — sans ce décalage, le logo apparaît décentré
              vers la droite (compensation d'une asymétrie propre au visuel du logo lui-même). */}
          <img src={logo} alt="" className="h-[50px] w-auto" style={{ transform: 'translateX(-13px)' }} />
        </a>
      )}

      {!hideHamburger && (
        <Button
          size="icon-lg" variant="ghost"
          onClick={onToggleSidebar}
          title={sidebarOpen ? translate(lang, 'reduceMenu') : translate(lang, 'openMenu')}
          className={cn('shrink-0', BTN_CLS)}
        >
          <Menu size={27} />
        </Button>
      )}

      {path && <span className="ml-2 text-sm text-[hsl(var(--sidebar-foreground))] min-w-0 truncate" dangerouslySetInnerHTML={{ __html: path }} />}

      <div className="flex-1" />

      {buttons.map(btn => {
        const overrides = buttonOverrides[btn.id]
        const hidden = overrides?.hidden ?? (btn.state === 'hidden')
        const effectiveBtn = { ...btn, badge: overrides?.badge ?? btn.badge, icon: overrides?.icon ?? btn.icon, tooltip: overrides?.tooltip ?? btn.tooltip }
        return (
          <div key={btn.id} style={hidden ? { display: 'none' } : undefined} className="shrink-0 flex items-center">
            <ToolbarButton btn={effectiveBtn} lang={lang} fontSize={fontSize} darkMode={darkMode} onEvent={onButtonEvent} />
          </div>
        )
      })}
    </header>
  )
}
